/**
 * Pure helpers for the Social wall and announcement comments: mention markup, plain-text excerpts
 * for notifications, and the department visibility predicate. No I/O, no Prisma, RN-safe.
 *
 * MENTION MARKUP. A composer writes a mention as `@[Display Name](person-uuid)`. The stored body
 * keeps that markup so the person behind a mention survives a rename; every reader renders it as
 * `@Display Name` (see `toMentionSegments`). Free text that is only a `@word` is NOT a mention.
 *
 * VISIBILITY. A post with no `audienceDepartmentIds` is org-wide. A targeted post is visible to
 * people whose current department is one of those ids, plus its author and admins (the caller
 * computes `isAuthor` / `isAdmin` and the viewer's current departments; this module stays pure).
 */

const MENTION_TOKEN = /@\[([^\]\r\n]{1,80})\]\(([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\)/g;

export const SOCIAL_POST_MAX_LENGTH = 4000;
export const SOCIAL_COMMENT_MAX_LENGTH = 2000;

/** Build the markup a composer inserts for one mention. */
export function mentionMarkup(displayName: string, personId: string): string {
  const safeName = displayName
    .replace(/[\[\]\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  return `@[${safeName}](${personId})`;
}

/** Distinct mentioned person ids, in first-appearance order, lower-cased. */
export function extractMentionPersonIds(text: string): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const match of text.matchAll(MENTION_TOKEN)) {
    const id = match[2]!.toLowerCase();
    if (!seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

/** The body with every mention reduced to `@Name` — what people read, and what DLP scans. */
export function stripMentionMarkup(text: string): string {
  return text.replace(MENTION_TOKEN, (_all, name: string) => `@${name}`);
}

export type MentionSegment =
  | { readonly type: 'text'; readonly text: string }
  | { readonly type: 'mention'; readonly name: string; readonly personId: string };

/** Split a body into plain text and mention chips, for rendering without any HTML. */
export function toMentionSegments(text: string): MentionSegment[] {
  const segments: MentionSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(MENTION_TOKEN)) {
    const index = match.index ?? 0;
    if (index > last) segments.push({ type: 'text', text: text.slice(last, index) });
    segments.push({ type: 'mention', name: match[1]!.trim(), personId: match[2]!.toLowerCase() });
    last = index + match[0].length;
  }
  if (last < text.length) segments.push({ type: 'text', text: text.slice(last) });
  return segments;
}

/** A one-line, mention-free preview of a body, for notification text. */
export function socialExcerpt(text: string, max = 140): string {
  const plain = stripMentionMarkup(text).replace(/\s+/g, ' ').trim();
  if (plain.length <= max) return plain;
  return `${plain.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

export interface DepartmentVisibilityInput {
  /** The post's audience. Empty = org-wide. */
  readonly audienceDepartmentIds: readonly string[];
  /** Departments the viewer is in on their own "today" (current assignments only). */
  readonly viewerDepartmentIds: readonly string[];
  readonly isAuthor: boolean;
  readonly isAdmin: boolean;
}

/** Whether a post is visible to a viewer. Authors and admins always see it. */
export function canSeeDepartmentPost(input: DepartmentVisibilityInput): boolean {
  if (input.isAuthor || input.isAdmin) return true;
  if (input.audienceDepartmentIds.length === 0) return true;
  const mine = new Set(input.viewerDepartmentIds.map((id) => id.toLowerCase()));
  return input.audienceDepartmentIds.some((id) => mine.has(id.toLowerCase()));
}

/**
 * The people who should receive a mention: deduped, minus the author (no self-notification), and
 * — for a targeted post — only those who can see the post. A mention must never reveal a
 * department-only post to someone outside its audience.
 */
export function mentionRecipients(input: {
  readonly mentionedPersonIds: readonly string[];
  readonly authorPersonId: string;
  readonly audienceDepartmentIds: readonly string[];
  /** Current department ids per mentioned person id. Missing key = no current department. */
  readonly departmentsByPersonId: ReadonlyMap<string, readonly string[]>;
}): string[] {
  const author = input.authorPersonId.toLowerCase();
  const out: string[] = [];
  for (const raw of input.mentionedPersonIds) {
    const id = raw.toLowerCase();
    if (id === author || out.includes(id)) continue;
    const visible = canSeeDepartmentPost({
      audienceDepartmentIds: input.audienceDepartmentIds,
      viewerDepartmentIds: input.departmentsByPersonId.get(id) ?? [],
      isAuthor: false,
      isAdmin: false,
    });
    if (visible) out.push(id);
  }
  return out;
}
